import { Link } from 'react-router-dom';
import Card from '@/components/ui/Card';
import LoadingStatus from '@/components/ui/LoadingStatus';
import {
  DashboardHero,
  DashboardMetricStrip,
  DashboardSection,
  MetricSummary,
  PlayerStatusList,
  ReportDigest,
  type PlayerStatusItem,
} from '@/components/dashboard/primitives';
import type { ScoutDashboardStats } from '@/types';

interface ScoutDashboardViewProps {
  data: ScoutDashboardStats;
  loading: boolean;
  error: string | null;
}

const formatRating = (rating: number) => `${Math.round(rating * 10) / 10}/100`;

export default function ScoutDashboardView({ data, loading, error }: ScoutDashboardViewProps) {
  if (loading) {
    return <LoadingStatus variant="panel" label="Loading player intelligence" className="h-64" />;
  }

  if (error) {
    return (
      <Card>
        <div role="alert" className="p-4 text-sm text-red-600 dark:text-red-300">
          Unable to load player intelligence. {error}
        </div>
      </Card>
    );
  }

  const reviewItems: PlayerStatusItem[] = [
    ...data.underPerformers.map((player) => ({
      id: `under-${player.id}`,
      name: player.name,
      value: formatRating(player.rating),
      status: 'Under performer',
      severity: 'warning' as const,
      to: `/players/${player.id}`,
    })),
    ...data.topPerformers.map((player) => ({
      id: `top-${player.id}`,
      name: player.name,
      value: formatRating(player.rating),
      status: 'Top performer',
      severity: 'success' as const,
      to: `/players/${player.id}`,
    })),
  ];

  const comparisonItems: PlayerStatusItem[] = data.playerComparisons.map((player) => ({
    id: player.id,
    name: player.name,
    value: formatRating(player.rating),
    to: `/players/${player.id}`,
  }));

  const trendLabel = `${data.performance.ratingTrend.charAt(0).toUpperCase()}${data.performance.ratingTrend.slice(1)}`;

  return (
    <div className="space-y-6">
      <DashboardHero
        title="Player intelligence briefing"
        supportingText="Performance summaries from the current workspace dashboard contract."
        accent="scout"
        primaryValue={data.totalPlayers}
        primaryLabel="Workspace players"
      />

      <DashboardMetricStrip
        ariaLabel="Scout intelligence metrics"
        metrics={[
          { label: 'Workspace players', value: data.totalPlayers },
          { label: 'Performance report average', value: formatRating(data.averageRating) },
          { label: 'Top performers', value: data.topPerformers.length },
          { label: 'Recent reports', value: data.recentReports.length },
        ]}
      />

      <DashboardSection
        title="Players to review"
        description="Top and underperformer summaries from performance reports."
      >
        <PlayerStatusList
          ariaLabel="Player performance summaries"
          items={reviewItems}
          emptyTitle="No performance summaries to review"
        />
      </DashboardSection>

      <DashboardSection
        title="Suggested from top performers"
        description="Comparison suggestions supplied by the scout dashboard."
        action={(
          <Link
            to="/comparison"
            className="inline-flex min-h-11 items-center rounded-md px-3 text-sm font-semibold text-club-blue-700 transition-colors hover:bg-club-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 motion-reduce:transition-none dark:text-club-blue-300 dark:hover:bg-club-blue-950"
          >
            Open player comparison
          </Link>
        )}
      >
        <PlayerStatusList
          ariaLabel="Suggested player comparisons"
          items={comparisonItems}
          emptyTitle="No comparison suggestions from top performers"
        />
      </DashboardSection>

      <DashboardSection
        title="Performance movement"
        description="Direction and average from performance reporting."
      >
        <MetricSummary
          label="Performance report average"
          value={formatRating(data.performance.averageRating)}
          status={trendLabel}
          context="Current performance reporting trend"
          progress={Math.max(0, Math.min(100, data.performance.averageRating))}
        />
      </DashboardSection>

      <DashboardSection
        title="Latest dashboard reports"
        description="Reports supplied by the dashboard contract."
      >
        <ReportDigest
          reports={data.recentReports}
          createLabel="Create scouting report"
          createTo="/reports/scouting/new"
          emptyTitle="No recent reports"
          reportLinkLabel={(report) => `Open ${report.type} report for ${report.playerName}`}
        />
      </DashboardSection>
    </div>
  );
}
